import { formatSalesOrderNumber } from '@order-management/format';
import {
  ALL_SALES_ORDER_RELATED_PERMISSIONS_GROUPS,
  PermissionsGroupName,
  UserPermissions,
} from '@order-management/roles';
import { SalesOrder, User, UserRoleType } from '@order-management/types';
import { useCallback, useMemo } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaPenToSquare } from 'react-icons/fa6';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import { remainingDays } from '../../shared/dates';
import useIsCustomerUser from '../../shared/hooks/is-customer-user';
import useIsUserEditable from '../../shared/hooks/is-user-editable';
import If from '../../shared/if/if';
import { formatRemainingDays } from '../../shared/utils';
import Button from '../../ui/button/button';
import Link from '../../ui/link/link';
import InfiniteTable, {
  InfiniteTableProps,
} from '../../ui/table/infinite-table/infinite-table';
import { Column, OnTableChangeFunction } from '../../ui/table/types';
import { Tooltip } from '../../ui/tooltip/tooltip';
import { UserStatusText } from '../types';

export type MyTeamTableProps = Omit<
  InfiniteTableProps<User>,
  'columns' | 'rowKey'
> & {
  onChange: OnTableChangeFunction<User, PermissionsGroupBySalesOrder>;
  handleEditUserClick?: (user: User) => void;
};

export type PermissionsGroupBySalesOrder = {
  permissionsGroupName: PermissionsGroupName;
  salesOrders: SalesOrder[];
};

type PermissionGroupBasicData = {
  name: PermissionsGroupName;
  expiresAt?: Date;
  salesOrder: SalesOrder;
};

export function MyTeamTable({
  dataSource,
  onChange,
  config,
  empty,
  handleEditUserClick,
  fetchMore,
  hasMore,
}: MyTeamTableProps) {
  const isCustomerUser = useIsCustomerUser();

  const { isEditableBySalesOrderRelatedUser } = useIsUserEditable();

  const isDisabledRow = useCallback((row: User) => row.deletedAt !== null, []);

  const hasDisabledText = useCallback(
    (row: User) => {
      if (isDisabledRow(row)) {
        return 'has-text-grey-light';
      }
    },
    [isDisabledRow]
  );

  const resolveSalesOrderTooltipMessage = useCallback(
    (permissionGroups: PermissionGroupBasicData[]) => {
      const permissionsGroupMessages = permissionGroups.map(
        (permissionGroup) => {
          if (permissionGroup.expiresAt) {
            const daysDiff = remainingDays(permissionGroup.expiresAt);
            return `${permissionGroup.name} - ${formatRemainingDays(daysDiff)}`;
          }
          return `${permissionGroup.name}`;
        }
      );
      return permissionsGroupMessages.join(', ');
    },
    []
  );

  const columns: Column<User, PermissionsGroupBySalesOrder>[] = useMemo(
    () => [
      {
        key: 'email',
        render: (el) => <Link to={`/users/${el.id}`}>{el.email}</Link>,
        className: 'is-width-20',
        title: 'User',
        sortable: true,
      },
      {
        key: 'salesOrders',
        className: 'is-width-60',
        render: (el) => {
          const salesOrderRelatedPermissionGroups = el.roles
            .filter((roleAssignment) => {
              if (isCustomerUser) {
                return roleAssignment.role.name === UserRoleType.CUSTOMER;
              } else {
                return (
                  roleAssignment.role.name === UserRoleType.ZSPACE_INTERNAL
                );
              }
            })
            .reduce((acc, roleAssignment) => {
              roleAssignment.permissionsGroupUserRoleAssignments.forEach(
                (permissionsGroupUserRoleAssignment) => {
                  const salesOrder =
                    permissionsGroupUserRoleAssignment.salesOrder;

                  if (salesOrder) {
                    const permissionGroupData = {
                      name: permissionsGroupUserRoleAssignment.permissionsGroup
                        .name as PermissionsGroupName,
                      expiresAt: permissionsGroupUserRoleAssignment.expiresAt,
                      salesOrder,
                    };
                    if (!acc[salesOrder.number]) {
                      acc[salesOrder.number] = [];
                    }
                    acc[salesOrder.number].push(permissionGroupData);
                  }
                }
              );
              return acc;
            }, {} as Record<string, PermissionGroupBasicData[]>);

          const parsedSalesOrderRelatedPermissionGroups = Object.values(
            salesOrderRelatedPermissionGroups
          );

          return (
            <Element display="inline-flex" alignItems="center" flexWrap="wrap">
              {parsedSalesOrderRelatedPermissionGroups.map(
                (permissionGroups) => {
                  const salesOrder = permissionGroups[0]?.salesOrder;
                  return (
                    <Tooltip
                      key={salesOrder?.id}
                      message={resolveSalesOrderTooltipMessage(
                        permissionGroups
                      )}
                    >
                      <Link
                        to={`/sales-orders/${salesOrder?.id}`}
                        underlined
                        color={'primary-dark'}
                      >
                        {formatSalesOrderNumber(salesOrder?.number)}
                      </Link>
                    </Tooltip>
                  );
                }
              )}
            </Element>
          );
        },
        title: 'Sales orders',
        sortable: false,
      },
      {
        key: 'lastActive',
        className: 'is-width-15',
        render: (el) => {
          const userStatus = el.lastActive
            ? UserStatusText.ACTIVE
            : UserStatusText.PENDING_FIRST_LOGIN;

          return <span className={hasDisabledText(el)}>{userStatus}</span>;
        },
        title: 'Status',
        sortable: false,
      },
      {
        key: 'actions',
        className: 'is-width-5',
        render: (el) => (
          <CheckPermissions
            permissions={UserPermissions.USERS_ASSIGN_ROLES}
            allowedPermissionGroups={ALL_SALES_ORDER_RELATED_PERMISSIONS_GROUPS}
          >
            <CheckPermissions.Render>
              <If condition={isEditableBySalesOrderRelatedUser(el)}>
                <Button
                  ml={1}
                  text
                  className="has-text-primary-dark is-size-7"
                  onClick={() => {
                    handleEditUserClick?.(el);
                  }}
                >
                  <Icon>
                    <FaPenToSquare />
                  </Icon>
                  <span className="is-underlined">Edit</span>
                </Button>
              </If>
            </CheckPermissions.Render>
          </CheckPermissions>
        ),
        title: '',
        sortable: false,
      },
    ],
    [
      isCustomerUser,
      resolveSalesOrderTooltipMessage,
      hasDisabledText,
      isEditableBySalesOrderRelatedUser,
      handleEditUserClick,
    ]
  );

  return (
    <InfiniteTable
      fetchMore={fetchMore}
      hasMore={hasMore}
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      config={config}
      empty={empty}
      rowKey={'id'}
      isDisabledSelection={isDisabledRow}
    />
  );
}

export default MyTeamTable;
